import React from 'react';
import { FileText, Download } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import DetailSection from '@components/common/DetailSection';
import Button from '@components/common/Button';
import { assessmentService } from '@services';
import { showToast } from '@utils/toast';
import { formatMediumDateTime } from '@utils/dateUtils';
import type { AssessmentReport } from '@app-types';
import styles from './AssessmentReportSection.module.css';

function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(1024));
  const size = bytes / Math.pow(1024, i);
  return `${size.toFixed(i > 0 ? 1 : 0)} ${units[i]}`;
}

interface AssessmentReportSectionProps {
  assessmentId: number;
}

const AssessmentReportSection: React.FC<AssessmentReportSectionProps> = ({
  assessmentId,
}) => {
  const { data: reports, isLoading } = useQuery({
    queryKey: ['assessment-reports', assessmentId],
    queryFn: () => assessmentService.getAssessmentReports(assessmentId),
    enabled: !!assessmentId,
  });

  const handleDownload = async (report: AssessmentReport) => {
    try {
      if (report.downloadUrl) {
        window.open(report.downloadUrl, '_blank');
      } else {
        const url = await assessmentService.getReportDownloadUrl(assessmentId, report.id);
        window.open(url, '_blank');
      }
    } catch {
      showToast.error('Failed to get download URL');
    }
  };

  const reportList = reports ?? [];

  let body: React.ReactNode;
  if (isLoading) {
    body = <div className={styles.loading}>Loading reports...</div>;
  } else if (reportList.length === 0) {
    body = (
      <div className={styles.emptyState}>
        <FileText size={24} />
        <p>No reports generated yet</p>
      </div>
    );
  } else {
    body = (
      <div className={styles.reportsList}>
        {reportList.map((report) => (
          <div key={report.id} className={styles.reportItem}>
            <FileText size={20} className={styles.reportIcon} />
            <div className={styles.reportInfo}>
              <div className={styles.reportName}>{report.originalName}</div>
              <div className={styles.reportMeta}>
                {formatFileSize(report.fileSize)}
                {' • '}
                {report.generatedByName || 'System'}
                {' • '}
                {formatMediumDateTime(report.createdAt)}
                {report.statusAtGeneration && (
                  <>
                    {' • '}
                    <span className={styles.statusBadge}>{report.statusAtGeneration}</span>
                  </>
                )}
              </div>
            </div>
            <div className={styles.reportActions}>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => handleDownload(report)}
                className={styles.actionButton}
              >
                <Download size={16} />
              </Button>
            </div>
          </div>
        ))}
      </div>
    );
  }

  return (
    <DetailSection title="Reports">
      <div className={styles.sectionContent}>
        <div className={styles.sectionHeader}>
          <span className={styles.reportCount}>
            <FileText size={16} />
            {reportList.length} report{reportList.length !== 1 ? 's' : ''}
          </span>
        </div>

        {body}
      </div>
    </DetailSection>
  );
};

export { AssessmentReportSection };
